import { AWARD_RECOMMENDED, AWARD_STATUS, FILENAMES, STEPS, SUBMIT_GRACE_TIME_MINUTES } from './constants';
import { ICourse } from './ICourse';
import { ICourseDeadlines } from './ICourseDeadlines';
import { ICourseModule } from './ICourseModule';
import { ICourseRoles } from './ICourseRoles';
import { IProvider } from './IProvider';
import { IMapping, IResultsSource } from './IResultsSource';
import { IWorkflowAction } from './IWorkflowAction';
import { getMomentDate } from './parse';
import { WorkflowTextService } from './WorkflowTextService';

const ALL_DEADLINE_KEYS: (keyof ICourseDeadlines)[] = [
  'step1DraftSubmissionDate',
  'step1InternalReviewDate',
  'step1FinalSubmissionDate',
  'step2DraftSubmissionDate',
  'step2InternalReviewDate',
  'step2FinalSubmissionDate'
];

export class WorkflowServiceV2 {

  public static getCourseSteps() {
    return [STEPS.STEP1, STEPS.REVIEW, STEPS.STEP2, STEPS.COMPLETED];
  }

  private static getNextDeadline(deadlines: ICourseDeadlines | undefined, target: keyof ICourseDeadlines) {

    if (!deadlines)
      return null;

    let index = ALL_DEADLINE_KEYS.indexOf(target);
    while (0 <= index && index < ALL_DEADLINE_KEYS.length) {
      const key = ALL_DEADLINE_KEYS[index];
      const value = deadlines[key];
      if (value) {
        return value;
      }
      index++;
    }

    return null;
  }

  /**
   * Calculate the current deadline based on course status.
   */
  public static getCurrentDeadline(courseStep: string | undefined, deadlines: ICourseDeadlines | undefined, hasBeenReviewed: boolean): Date | null | undefined {
    if (!deadlines || !courseStep) {
      return null;
    }

    // Parse the course step
    const [step, status] = courseStep.split('|').map(s => s.trim());

    switch (step) {
      case STEPS.STEP1:
        if (status === 'approved') {
          return WorkflowServiceV2.getNextDeadline(deadlines, 'step2DraftSubmissionDate');
        } else if (hasBeenReviewed) {
          return WorkflowServiceV2.getNextDeadline(deadlines, 'step1FinalSubmissionDate');
        } else {
          switch (status) {
            case 'draft':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step1DraftSubmissionDate');
            case 'submitted':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step1InternalReviewDate');
            case 'rejected':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step1FinalSubmissionDate');
          }
        }
        break;

      case STEPS.STEP2:
        if (status === 'approved') {
          return null;
        } else if (hasBeenReviewed) {
          return WorkflowServiceV2.getNextDeadline(deadlines, 'step2FinalSubmissionDate');
        } else {
          switch (status) {
            case 'draft':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step2DraftSubmissionDate');
            case 'submitted':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step2InternalReviewDate');
            case 'rejected':
              return WorkflowServiceV2.getNextDeadline(deadlines, 'step2FinalSubmissionDate');
          }
        }
        break;

      case STEPS.REVIEW:
        return WorkflowServiceV2.getNextDeadline(deadlines, 'step2DraftSubmissionDate');

      case STEPS.COMPLETED:
        return null;
    }

    return null;
  }

  public static getNextStatus(moduleId: STEPS, action: string | undefined, results: { [key: string]: any }): string {
    switch (action) {
      case 'edit':
        switch (moduleId) {
          case STEPS.STEP1:
            return `${moduleId} | submitted`;
          case STEPS.REVIEW:
            return `${moduleId} | submitted`;
          case STEPS.STEP2:
            return `${moduleId} | submitted`;
          case STEPS.COMPLETED:
            return STEPS.COMPLETED;
          default:
            return '';
        }

      case 'review':
        switch (moduleId) {

          case STEPS.STEP1:
            {
              const s = WorkflowTextService.parseStatus(results.ApprovalSTEP1);
              if (s) {
                return WorkflowTextService.formatStatus(s);
              }
            }
            break;

          case STEPS.REVIEW:
            {
              const s = WorkflowTextService.parseStatus(results.ApprovalREVIEW);
              if (s) {
                return WorkflowTextService.formatStatus(s);
              }
            }
            break;

          case STEPS.STEP2:
            {
              const s = WorkflowTextService.parseStatus(results.ApprovalSTEP2);
              if (s) {
                return WorkflowTextService.formatStatus(s);
              }
            }
            break;
        }
        break;
    }

    return '';
  }

  static isStepCompleted(moduleId: string, courseStatus: string): boolean {
    switch (moduleId) {
      case STEPS.STEP1:
        return courseStatus.startsWith(STEPS.REVIEW) || courseStatus.startsWith(STEPS.STEP2) || courseStatus.startsWith('COMPLETED');
      case STEPS.REVIEW:
        return courseStatus.startsWith(STEPS.STEP2) || courseStatus.startsWith('COMPLETED');
      case STEPS.STEP2:
        return courseStatus.startsWith('COMPLETED');
      default:
        return false;
    }
  }

  public static getWorkflowModifyAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {

    const courseStep = course.CourseStep;

    switch (moduleId) {
      case STEPS.STEP1:
        if (roles.isStaff && !roles.isAdmin) {
          return;
        }
        if (!courseStep || courseStep === `${STEPS.STEP1} | draft`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Step 1" : "Start Step 1",
            tooltip: isCreated ? "Continue the Step 1 questionnaire" : "Start the Step 1 questionnaire"
          }
        } else {
          break;
        }
      case STEPS.REVIEW:
        if (!roles.isExternalExpert) {
          return;
        }
        if (courseStep === `${STEPS.REVIEW} | draft` || courseStep === `${STEPS.STEP1} | approved`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Course Review" : "Start Course Review",
            tooltip: isCreated ? "Continue the Course Review" : "Start the Course Review"
          }
        } else {
          break;
        }
      case STEPS.STEP2:
        if (roles.isStaff && !roles.isAdmin) {
          return;
        }
        if (roles.isExternalExpert && !roles.isStaff) {
          return;
        }
        if (courseStep === `${STEPS.STEP2} | draft` || courseStep === `${STEPS.REVIEW} | approved`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Step 2" : "Start Step 2",
            tooltip: isCreated ? "Continue the Step 2 questionnaire" : "Start the Step 2 questionnaire"
          }
        } else {
          break;
        }
      case STEPS.COMPLETED:
        if (courseStep !== STEPS.COMPLETED || course.LabelAwarded !== AWARD_STATUS.AWARDED) {
          return;
        }
        if (roles.isStaff && !roles.isAdmin) {
          return;
        }
        return {
          id: isCreated ? 'edit' : 'new-small-changes',
          title: isCreated ? "Continue with Small Changes" : "Sumbit Small Changes",
          tooltip: isCreated ? "Continue with the questionnaire to submit small changes" : "Submit small changes to the questionnaire"
        }
    }

    if (!roles.isStaff && !roles.isAdmin) {
      const dateModified = getMomentDate(course.modified);
      const dateNow = getMomentDate(new Date());
      if (dateModified && dateNow && courseStep.startsWith(moduleId) && courseStep.endsWith('submitted')) {
        const diff = dateNow.diff(dateModified, 'minutes');
        if (diff < SUBMIT_GRACE_TIME_MINUTES)
          return {
            id: 'edit',
            title: `Resubmit`,
            tooltip: `You can modify and resubmit the questionnaire within ${SUBMIT_GRACE_TIME_MINUTES - diff} minutes.`
          }
      }
    }

    if (moduleId === STEPS.STEP1 && (courseStep === `${STEPS.STEP1} | rejected` || courseStep === `${STEPS.REVIEW} | rejected`)
      || moduleId === STEPS.STEP2 && courseStep === `${STEPS.STEP2} | rejected`) {
      return {
        id: 'edit',
        title: `Correct & Resubmit`,
        tooltip: `Correct and resubmit the questionnaire.`
      }
    }
  }

  public static getWorkflowReviewAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {

    const courseStatus = course?.CourseStep;

    if (!courseStatus || !isCreated) {
      return;
    }

    const moduleName = WorkflowTextService.getModuleName(moduleId);

    if (roles.isStaff && courseStatus.startsWith(moduleId)) {
      if (courseStatus.endsWith('draft'))
        return;
      if (courseStatus.endsWith('submitted')) {
        return {
          id: 'review',
          title: moduleId === STEPS.REVIEW ? `Check ${moduleName}` : `Review ${moduleName}`,
          tooltip: `Review and provide feedback for the ${moduleName} questionnaire.`
        }
      }
    };
  }

  public static getStepDescription(course: ICourse, roles: ICourseRoles, moduleId: string): string {
    switch (moduleId) {
      case STEPS.STEP1:
        return "Pre-course self assessment for EIT Label application";
      case STEPS.REVIEW:
        return `Course Review by External Reviewer ${WorkflowTextService.formatExpertLink(course.expert, roles)}`;
      case STEPS.STEP2:
        return "Post-course self assessment for EIT Label application";
      default:
        return '';
    };
  }

  public static getDetailedStepDescription(course: ICourse, roles: ICourseRoles) {
    switch (course?.CourseStep) {
      case `${STEPS.STEP1} | draft`:
        return roles.isStaff
          ? {
            text: `Waiting for the course provider to submit the Step 1 questionnaire.` +
              (course.AwardRecommended === AWARD_RECOMMENDED.NOT_RECOMMENDED
                ? '<div style=\'font-weight: normal; margin-top: 10px;\'>Please note that this course has been returned. Please take a look at the reviewer\'s feedback when submitting the update.</div>'
                : ''),
            color: 'yellow'
          }
          : {
            text: `Please submit the Step 1 questionnaire to continue the application process.` +
              (course.AwardRecommended === AWARD_RECOMMENDED.NOT_RECOMMENDED
                ? '<div style=\'font-weight: normal; margin-top: 10px;\'>Please note that this course has been returned. Please take a look at the reviewer\'s feedback when submitting the update.</div>'
                : ''),
            color: 'brand'
          };
      case `${STEPS.STEP1} | submitted`:
        return roles.isStaff
          ? { text: "Please review the Step 1 questionnaire and provide the feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the Step 1 questionnaire and provide feedback.", color: 'yellow' };
      case `${STEPS.STEP1} | approved`:
        if (course.expert) {
          return roles.isExternalExpert
            ? { text: `Please review this course and submit your recommendations.`, color: 'blue' }
            : { text: `Waiting for the invited external reviewer ${WorkflowTextService.formatExpertLink(course.expert, roles)} to review this course.`, color: 'yellow' };
        } else {
          return roles.isStaff
            ? { text: `Please assign an external expert for this course.`, color: 'blue' }
            : { text: `Waiting for the EIT UM staff to assign an external reviewer to review this course.`, color: 'yellow' };
        }
      case `${STEPS.STEP1} | rejected`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to correct the Step 1 questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Step 1 questionnaire needs some correction. Please correct it and submit it again.", color: 'blue' };

      case `${STEPS.REVIEW} | draft`:
        if (course.expert) {
          return roles.isExternalExpert
            ? { text: `Please review this course and submit your recommendations.`, color: 'blue' }
            : { text: `Waiting for the invited external reviewer ${WorkflowTextService.formatExpertLink(course.expert, roles)} to review this course.`, color: 'yellow' };
        } else {
          return roles.isStaff
            ? { text: `Please assign an external expert for this course.`, color: 'blue' }
            : { text: `Waiting for the EIT UM staff to assign an external reviewer to review this course.`, color: 'yellow' };
        }
      case `${STEPS.REVIEW} | approved`:
        return (roles.isStaff || roles.isExternalExpert)
          ? { text: "Waiting for the course provider to start the Step 2 questionnaire.", color: 'yellow' }
          : { text: "Please submit the Step 2 questionnaire to continue the application process.", color: 'brand' };
      case `${STEPS.REVIEW} | rejected`:
        return (roles.isStaff)
          ? { text: "Waiting for the course provider to correct the questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Review needs some correction. Please correct it and submit it again.", color: 'blue' };
      case `${STEPS.REVIEW} | submitted`:
        return (roles.isStaff)
          ? { text: "Please review the External Reviewer's Feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the External Reviewer's Feedback.", color: 'yellow' };

      case `${STEPS.STEP2} | draft`:
        return (roles.isStaff || roles.isExternalExpert)
          ? { text: "Waiting for the course provider to submit the Step 2 questionnaire.", color: 'yellow' }
          : { text: "Please submit the Step 2 questionnaire to continue the application process.", color: 'brand' };
      case `${STEPS.STEP2} | submitted`:
        return (roles.isStaff || roles.isExternalExpert)
          ? { text: "Please review the Step 2 questionnaire and provide the feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the Step 2 questionnaire and provide feedback.", color: 'yellow' };
      case `${STEPS.STEP2} | approved`:
        return (roles.isStaff || roles.isExternalExpert)
          ? { text: "Waiting for the course provider to start the Step 3 questionnaire.", color: 'yellow' }
          : { text: "The Step 2 questionnaire has been approved. You can start the Step 3 questionnaire.", color: 'blue' }
      case `${STEPS.STEP2} | rejected`:
        return (roles.isStaff || roles.isExternalExpert)
          ? { text: "Waiting for the course provider to correct the Step 2 questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Step 2 questionnaire needs some correction. Please correct it and submit it again.", color: 'blue' };

      case `COMPLETED`:
        if (course?.LabelAwarded === AWARD_STATUS.AWARDED) {
          return {
            text: `This submission has been completed. The EIT UM Label has been awarded.<br>
            <div style='font-weight: normal; margin-top: 10px;'>
                You can download from here the <a style='text-decoration: underline' href='/api/docs/Certificate of Completion template.pptx' target='_blank'>EIT Label Certificate template</a>.
              </div>
            `, color: 'green'
          }
        } else if (course?.LabelAwarded === AWARD_STATUS.AWARDED_EDITION) {
          return {
            text: `This submission has been completed. The EIT UM Label has been awarded for this edition of the course.
            <div style='font-weight: normal; margin-top: 10px;'>
                You can download from here the <a style='text-decoration: underline' href='/api/docs/Certificate of Completion template.pptx' target='_blank'>EIT Label Certificate template</a>.
              </div>
            `, color: 'green'
          }
        } else if (course?.LabelAwarded === AWARD_STATUS.NOT_AWARDED) {
          return {
            text: `This submission has been completed. The EIT UM Label has NOT been awarded.`,
            color: 'gray'
          }
        } else {
          return { text: `This submission has been completed.`, color: 'gray' }
        }
    };
  }

  public static isAssignToExpertEnabled(course: ICourse, roles: ICourseRoles) {
    const courseStep = course.CourseStep;
    return ([STEPS.REVIEW, STEPS.STEP2, STEPS.COMPLETED].some(x => courseStep.startsWith(x)) || course.CourseStep === `${STEPS.STEP1} | approved`) && !!roles.isStaff;
  }

  public static getNextModule(moduleId: string | undefined) {
    switch (moduleId) {
      case STEPS.STEP1:
        return STEPS.REVIEW;
      case STEPS.REVIEW:
        return STEPS.STEP2;
    }
  }

  public static getCourseData(course: ICourse) {
    const data: any = {
      ...course
    }

    // overwrite default provider data with course data if available
    if (course.CourseProviderUsers?.length) {
      const firstUser = course.CourseProviderUsers[0];
      data.ProviderContact = firstUser.name;
      data.ProviderEmail = firstUser.email;
    }

    if (course.expert) {
      data.ReviewerName = course.expert?.name;
    }

    return data;
  }

  public static getResultSources(
    course: ICourse,
    module: ICourseModule,
    template: any,
    provider: IProvider,
  ): IResultsSource[] {

    const courseId = course.id;
    const moduleId = module.id;
    const mapping = WorkflowTextService.getDefaultMapping(template, [/Date$/]);

    const sources: IResultsSource[] = [];

    sources.push({ data: WorkflowTextService.getDefaultResults(template), mapping });

    if (course.coursePreviousRunId && moduleId === STEPS.STEP1) {
      const previousRunMapping = WorkflowTextService.getDefaultMapping(template, [/^Approval/, /^Submit/, /^Feedback/, /^Award/, /^Expert/, /Date$/, /^File/, /Upload$/]);
      const previousRunMappingV1 = this.getMappingV1();

      sources.push({ courseId: course.coursePreviousRunId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping: previousRunMapping, mappingV1: previousRunMappingV1 });
      sources.push({ courseId: course.coursePreviousRunId, moduleId: STEPS.STEP2, fileName: FILENAMES.RESULTS, mapping: previousRunMapping, mappingV1: previousRunMappingV1 });
    }

    sources.push({ data: provider, mapping });
    sources.push({ data: this.getCourseData(course), mapping });

    if (moduleId === STEPS.REVIEW) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
    }

    if (moduleId === STEPS.STEP2) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.REVIEW, fileName: FILENAMES.RESULTS, mapping });
    }

    if (module.id === STEPS.COMPLETED) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.REVIEW, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.STEP2, fileName: FILENAMES.RESULTS, mapping });
    }

    return sources;
  }

  public static getFeedbackSources(
    course: ICourse,
    module: ICourseModule,
    template: any,
    provider: IProvider,
  ): IResultsSource[] {

    const courseId = course.id;
    const mapping = WorkflowTextService.getDefaultMapping(template, []);

    const sources: IResultsSource[] = [];

    sources.push({ data: WorkflowTextService.getDefaultResults(template), mapping });
    sources.push({ data: provider, mapping });
    sources.push({ data: this.getCourseData(course), mapping });

    if (module.id === STEPS.STEP1) {
      if (course.AwardRecommended === AWARD_RECOMMENDED.NOT_RECOMMENDED) {
        sources.push({ courseId, moduleId: STEPS.REVIEW, fileName: FILENAMES.RESULTS, mapping });
        sources.push({ courseId, moduleId: STEPS.REVIEW, fileName: FILENAMES.WORKFLOW, mapping });
      } else {
        sources.push({ data: module.workflow, mapping });
      }
    }

    if (module.id === STEPS.REVIEW) {
      sources.push({ data: module.results, mapping });
      sources.push({ data: module.workflow, mapping });
    }

    if (module.id === STEPS.STEP2) {
      sources.push({ data: module.workflow, mapping });
    }

    return sources;
  }

  public static getDraftStatus(courseStep: string | undefined): string {
    switch (courseStep) {
      case `${STEPS.STEP1} | approved`: return `${STEPS.REVIEW} | draft`;
      case `${STEPS.STEP1} | rejected`: return `${STEPS.STEP1} | draft`;
      case `${STEPS.REVIEW} | approved`: return `${STEPS.STEP2} | draft`;
      case `${STEPS.REVIEW} | rejected`: return `${STEPS.STEP1} | draft`;
      case `${STEPS.STEP2} | rejected`: return `${STEPS.STEP2} | draft`;
      default: return '';
    }
  }

  // TODO: remove this method when all courses are migrated to V2
  // This method patches the old course mapping to match the new template structure
  // It should be called only once when the course is migrated to V2
  // and should not be used for new courses
  // The method modifies the previousRunMapping object in place
  private static getMappingV1(): IMapping {

    const result: IMapping = {};

    // typo in V1
    result['CourseDliveryMode'] = {
      dst: 'CourseDeliveryMode',
    }

    result['CourseChallengeAreas'] = { dst: 'CourseChallengeAreas' }

    result['CourseDescription'] = { dst: 'CourseDescription' }
    result['CourseTitle'] = { dst: 'CourseTitle' }
    result['ProviderAddress'] = { dst: 'ProviderAddress' }
    result['ProviderConsortium'] = { dst: 'ProviderConsortium' }
    result['ProviderContact'] = { dst: 'ProviderContact' }
    result['ProviderEmail'] = { dst: 'ProviderEmail' }
    result['ProviderName'] = { dst: 'ProviderName' }
    result['ProviderWebsite'] = { dst: 'ProviderWebsite' }
    result['ProviderPhone'] = { dst: 'ProviderPhone' }

    // map old course levels to new ones
    result['CourseLevel'] = {
      dst: 'CourseLevel',
      mapValue: (value: any) => {
        switch (value) {
          case 'awareness': return 'Level_1'
          case 'problem_solving': return 'Level_2'
          case 'application': return 'Level_3'
          default: return value;
        }
      }
    }

    // map old course principles to new ones
    result['Principles1'] = { dst: 'KnowledgeTriangleIntegration' }
    result['Principles2'] = { dst: 'Innovationandentrepreneurship education' }
    result['Principles3'] = { dst: 'Highly integrated, innovative learning-by-doing curriculum' }
    result['Principles4'] = { dst: 'Internationalengagement/exposure' }
    result['Principles5'] = { dst: 'Inter-sectoral and inter-organisational experience/exposure' }
    result['Principles6'] = { dst: 'Geographic inclusion' }
    result['Principles7'] = { dst: 'Inclusion, diversity and gender mainstreaming and equality' }

    const mapOLO = (value: string) => {
      switch (value) {
        case 'OLO1': return 'Entrepreneurship';
        case 'OLO2': return 'Innovation_and_technology';
        case 'OLO3': return ' Creativity'; // TODO: fix the template when possible
        case 'OLO4': return 'Intercultural';
        case 'OLO5': return 'Making value judgments and sustainability'; // TODO: fix the template when possible
        case 'OLO6': return 'Leadership';
        default: return value;
      }
    }

    // map old course intended learning outcomes to new ones
    result['CourseIntendedLearningOutcome'] = {
      dst: 'ILOs_OLOs',
      mapValue: (value: any) => {
        if (Array.isArray(value)) {
          return value.map((v: { ILOID: string, ILO: string, OLO: string }) => {
            return {
              ILOID: v.ILOID,
              ILO: v.ILO,
              ILOTEXT: `${v.ILOID}: ${v.ILO}`,
              OLO: mapOLO(v.OLO),
            }
          })
        } else {
          return value;
        }
      }
    }

    return result;
  }
}